import { ContentLanguage, Language, CurrencyCode } from 'codegen/types'

/** Marketplace / My Account language switcher + next-intl locales. */
export const languageMap = new Map<Language, string>([
  [Language.Ms, 'Bahasa Melayu'],
  [Language.De, 'Deutsch'],
  [Language.Et, 'Eesti'],
  [Language.En, 'English'],
  [Language.Es, 'Español'],
  [Language.Fr, 'Français'],
  [Language.It, 'Italiano'],
  [Language.Lv, 'Latviešu'],
  [Language.Lt, 'Lietuvių'],
  [Language.Nl, 'Nederlands'],
  [Language.Pl, 'Polski'],
  [Language.Pt, 'Português (BR)'],
])

/**
 * Studio language switcher only. Includes Language Push locales that are not
 * enabled for marketplace / My Account routing yet.
 */
export const studioLanguageMap = new Map<Language, string>([
  [Language.Az, 'Azərbaycan'],
  [Language.Id, 'Bahasa Indonesia'],
  [Language.Ms, 'Bahasa Melayu'],
  [Language.Bn, 'বাংলা'],
  [Language.Bg, 'Български'],
  [Language.Cs, 'Čeština'],
  [Language.Da, 'Dansk'],
  [Language.De, 'Deutsch'],
  [Language.Et, 'Eesti'],
  [Language.El, 'Ελληνικά'],
  [Language.En, 'English'],
  [Language.Es, 'Español'],
  [Language.Fr, 'Français'],
  [Language.Hi, 'हिन्दी'],
  [Language.Hr, 'Hrvatski'],
  [Language.It, 'Italiano'],
  [Language.Ja, '日本語'],
  [Language.Kk, 'Қазақша'],
  [Language.Ko, '한국어'],
  [Language.Sw, 'Kiswahili'],
  [Language.Lv, 'Latviešu'],
  [Language.Lt, 'Lietuvių'],
  [Language.Hu, 'Magyar'],
  [Language.Nl, 'Nederlands'],
  [Language.No, 'Norsk'],
  [Language.Uz, 'Oʻzbekcha'],
  [Language.Pa, 'ਪੰਜਾਬੀ'],
  [Language.Pl, 'Polski'],
  [Language.Pt, 'Português (BR)'],
  [Language.Ro, 'Română'],
  [Language.Ru, 'Русский'],
  [Language.Sk, 'Slovenčina'],
  [Language.Fi, 'Suomi'],
  [Language.Sv, 'Svenska'],
  [Language.Th, 'ไทย'],
  [Language.Vi, 'Tiếng Việt'],
  [Language.Tr, 'Türkçe'],
  [Language.Uk, 'Українська'],
  [Language.ZhHans, '中文 (简体)'],
  [Language.ZhHant, '中文 (繁體)'],
])

/**
 * Locales the account actually stores.
 *
 * The GraphQL `Language` enum accepts every Language Push locale, but the
 * backend validates against its own allowlist and answers `success: null` with
 * `fields: [{ field: 'language', errors: ['users.validation.in'] }]` for the
 * ones it does not know yet. This list is therefore a separate fact from both
 * `languageMap` (which locales marketplace routes) and `studioLanguageMap`
 * (which locales we have translations for).
 *
 * Confirm a locale persists before adding it:
 *
 *     mutation { usersUpdateLanguage(language: ja) { success errors { code message } } }
 *
 * `success: true` means it stores; `success: null` means it does not.
 */
export const persistableLanguages = new Set<Language>([
  Language.Bg,
  Language.Bn,
  Language.Cs,
  Language.Da,
  Language.De,
  Language.El,
  Language.En,
  Language.Es,
  Language.Et,
  Language.Fi,
  Language.Fr,
  Language.Hi,
  Language.Hu,
  Language.Id,
  Language.It,
  Language.Ja,
  Language.Kk,
  Language.Ko,
  Language.Lt,
  Language.Lv,
  Language.Ms,
  Language.Nl,
  Language.No,
  Language.Pa,
  Language.Pl,
  Language.Pt,
  Language.Ro,
  Language.Sk,
  Language.Sv,
  Language.Sw,
  Language.Th,
  Language.Tr,
  Language.Uk,
  Language.Uz,
  Language.Vi,
  Language.ZhHans,
  Language.ZhHant,
])

/**
 * Locales the Studio language switcher offers right now.
 *
 * Picking a locale the account cannot store would translate the UI until the
 * next load and then silently revert, so the picker is limited to the ones that
 * persist. The rest of `studioLanguageMap` (cookie validation, translation
 * loading, Intl normalisation) keeps working, so a locale set by other means
 * still renders.
 */
export const studioSelectableLanguageMap = new Map<Language, string>(
  [...studioLanguageMap].filter(([language]) => persistableLanguages.has(language)),
)

/**
 * Marketplace / My Account locales — the 12 in `languageMap`, not every locale
 * Studio serves. Despite the name, this is one of two locale sets: a surface
 * that serves the Studio set (studio, freemode, lumo and their landing pages)
 * wants `studioUrlLocaleSegments` from `./helpers`, and reaching for this one
 * there silently under-serves 28 locales — in routing it 404s them (#13848), in
 * SEO metadata it drops them from their own alternates cluster (STU-9739).
 */
// oxlint-disable-next-line unicorn/prefer-spread
export const supportedLanguages = Array.from(languageMap.keys())

export const defaultLanguage = Language.En

export const defaultCurrency = {
  code: CurrencyCode.Usd,
  symbol: '$',
}

export const defaultContentLanguage = ContentLanguage.LanguageEn

export const mapLocaleToContentLanguage = new Map<string, ContentLanguage>([
  [Language.En, ContentLanguage.LanguageEn],
  [Language.Es, ContentLanguage.LanguageEs],
  [Language.De, ContentLanguage.LanguageDe],
  [Language.Fr, ContentLanguage.LanguageFr],
  [Language.It, ContentLanguage.LanguageIt],
  [Language.Nl, ContentLanguage.LanguageNl],
  [Language.Pt, ContentLanguage.LanguagePtBr],
  [Language.Pl, ContentLanguage.LanguagePl],
])

export const mapContentLanguageToLocale = new Map<ContentLanguage, string>([
  [ContentLanguage.LanguageEn, Language.En],
  [ContentLanguage.LanguageEs, Language.Es],
  [ContentLanguage.LanguageDe, Language.De],
  [ContentLanguage.LanguageFr, Language.Fr],
  [ContentLanguage.LanguageIt, Language.It],
  [ContentLanguage.LanguageNl, Language.Nl],
  [ContentLanguage.LanguagePtBr, Language.Pt],
  [ContentLanguage.LanguagePl, Language.Pl],
])

export const mapLocaleToLanguageCode = new Map<Language, string>([
  [Language.En, 'en-US'],
  [Language.Es, 'es-ES'],
  [Language.De, 'de-DE'],
  [Language.Fr, 'fr-FR'],
  [Language.It, 'it-IT'],
  [Language.Nl, 'nl-NL'],
  [Language.Pt, 'pt-BR'],
  [Language.Pl, 'pl-PL'],
  [Language.Lt, 'lt-LT'],
  [Language.Lv, 'lv-LV'],
  [Language.Et, 'et-EE'],
  [Language.Ms, 'ms-MY'],
  [Language.Ja, 'ja-JP'],
  [Language.Ko, 'ko-KR'],
  [Language.ZhHans, 'zh-CN'],
  [Language.ZhHant, 'zh-TW'],
  [Language.Tr, 'tr-TR'],
  [Language.Id, 'id-ID'],
  [Language.Vi, 'vi-VN'],
  [Language.Th, 'th-TH'],
  [Language.Hi, 'hi-IN'],
  [Language.Uk, 'uk-UA'],
  [Language.Pa, 'pa-IN'],
  [Language.Ro, 'ro-RO'],
  [Language.Cs, 'cs-CZ'],
  [Language.Hu, 'hu-HU'],
  [Language.El, 'el-GR'],
  [Language.Sv, 'sv-SE'],
  [Language.No, 'nb-NO'],
  [Language.Da, 'da-DK'],
  [Language.Fi, 'fi-FI'],
  [Language.Sk, 'sk-SK'],
  [Language.Bg, 'bg-BG'],
  [Language.Sw, 'sw-KE'],
  [Language.Kk, 'kk-KZ'],
  [Language.Ru, 'ru-RU'],
  [Language.Hr, 'hr-HR'],
  [Language.Bn, 'bn-BD'],
  [Language.Uz, 'uz-UZ'],
  [Language.Az, 'az-AZ'],
])
